<template>
  <Teleport to="body">
    <div v-if="modelValue" class="fixed inset-0 z-[70] overflow-y-auto">
      <div class="fixed inset-0 bg-black/50" @click="close"></div>
      <div class="relative flex min-h-screen items-center justify-center p-4">
        <div class="relative flex max-h-[92vh] w-full max-w-2xl flex-col overflow-hidden rounded-lg bg-white shadow-xl">
          <header class="flex items-start justify-between gap-4 border-b border-slate-200 px-6 py-4">
            <div>
              <h3 class="text-lg font-semibold text-slate-900">{{ modalTitle }}</h3>
              <p class="mt-1 text-sm text-slate-500">
                {{ isEditMode ? '调整当月客户额度、利润率和区域。' : '添加客户到当前月度方案，主档不存在时由后端创建。' }}
              </p>
            </div>
            <button
              type="button"
              class="rounded-md p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
              @click="close"
            >
              <span class="sr-only">关闭</span>
              <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </header>

          <main class="min-h-0 flex-1 overflow-y-auto px-6 py-5">
            <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div v-if="isEditMode" class="monthly-company-summary md:col-span-2 rounded-lg border border-slate-200 bg-slate-50 px-4 py-3">
                <span class="form-label">公司名称</span>
                <div class="mt-1 truncate text-sm font-medium text-slate-900">{{ form.companyName || '-' }}</div>
              </div>

              <label v-else class="block md:col-span-2">
                <span class="form-label">公司名称 <span class="text-red-500">*</span></span>
                <input
                  v-model="form.companyName"
                  type="text"
                  class="form-input mt-1 w-full"
                  placeholder="请输入公司名称"
                >
              </label>

              <label v-if="!isEditMode" class="block">
                <span class="form-label">注册地</span>
                <input
                  v-model="form.registeredRegion"
                  type="text"
                  class="form-input mt-1 w-full"
                  placeholder="请输入注册地"
                >
              </label>

              <label class="block">
                <span class="form-label">
                  名单区域
                  <span v-if="regionRequired" class="text-red-500">*</span>
                </span>
                <el-select
                  :model-value="form.regionBucketId ?? ''"
                  class="form-select mt-1 w-full"
                  @change="selectRegion"
                >
                  <el-option :label="regionRequired ? '请选择名单区域' : '按注册地自动归类'" value="" />
                  <el-option
                    v-for="region in regionOptions"
                    :key="region.id"
                    :label="region.regionName || '未命名区域'"
                    :value="region.id"
                  />
                </el-select>
              </label>

              <label class="block">
                <span class="form-label">目标额度 <span class="text-red-500">*</span></span>
                <input
                  v-model="form.targetOutputAmount"
                  type="number"
                  min="0"
                  step="100"
                  class="form-input mt-1 w-full"
                  placeholder="请输入目标额度"
                  @input="amountWarning = ''"
                >
                <p v-if="amountWarning" class="mt-1 text-xs text-amber-600">{{ amountWarning }}</p>
              </label>

              <label class="block">
                <span class="form-label">利润率（%） <span class="text-red-500">*</span></span>
                <input
                  v-model="form.targetProfitRatePercent"
                  type="number"
                  step="0.01"
                  min="0"
                  class="form-input mt-1 w-full"
                  placeholder="请输入利润率"
                >
                <p v-if="isFullProfitRate" class="mt-1 text-xs text-amber-600">100% 利润率客户只能作为链路第一个主体</p>
              </label>

              <div class="md:col-span-2">
                <InvoiceProductQuotaEditor
                  v-model="form.outputProductQuotas"
                  title="销项品名组结构"
                  :target-amount="normalizedTargetAmount"
                  class="rounded-lg border border-slate-200 p-3"
                />
              </div>

              <div class="md:col-span-2">
                <InvoiceProductQuotaEditor
                  v-model="form.inputProductQuotas"
                  title="进项品名组结构"
                  :target-amount="normalizedTargetAmount"
                  class="rounded-lg border border-slate-200 p-3"
                />
              </div>

              <div class="md:col-span-2">
                <InvoiceCustomerInvoiceRulePanel
                  :customer="invoiceRuleCustomer"
                  :downstream-allocations="downstreamAllocations"
                  :downstream-tag-preferences="downstreamTagPreferences"
                  :disabled="submitting"
                  @configure-downstream="emit('configure-downstream', $event)"
                  @configure-tag-preference="emit('configure-tag-preference', $event)"
                />
              </div>
            </div>
          </main>

          <footer class="flex justify-end gap-3 border-t border-slate-200 px-6 py-4">
            <button type="button" class="btn-secondary" :disabled="submitting" @click="close">取消</button>
            <button type="button" class="btn-primary" v-permission="'enterprise-invoice:plan-customer:manage'" :disabled="submitting" @click="submit">
              {{ submitting ? '保存中...' : '保存' }}
            </button>
          </footer>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
import { computed, reactive, ref, watch } from 'vue'
import { ElMessage } from 'element-plus'
import {
  addEnterpriseInvoicePlanCustomer,
  updateEnterpriseInvoicePlanCustomer
} from '@/api/invoice'
import { formatInvoiceAmount, normalizeAmountInput, parsePercentInput, toPercentInput } from '../utils'
import {
  defaultProductQuotas,
  normalizeProductQuotas,
  quotaTotalMatched,
  validateProductQuotaList
} from '../productQuota'
import InvoiceCustomerInvoiceRulePanel from './InvoiceCustomerInvoiceRulePanel.vue'
import InvoiceProductQuotaEditor from './InvoiceProductQuotaEditor.vue'

const props = defineProps({
  modelValue: { type: Boolean, default: false },
  planId: { type: [Number, String], default: null },
  customer: { type: Object, default: null },
  regions: { type: Array, default: () => [] },
  downstreamAllocations: { type: Array, default: () => [] },
  downstreamTagPreferences: { type: Array, default: () => [] }
})

const emit = defineEmits(['update:modelValue', 'saved', 'configure-downstream', 'configure-tag-preference'])

const form = reactive({
  id: null,
  customerId: null,
  companyName: '',
  registeredRegion: '',
  regionBucketId: null,
  targetOutputAmount: null,
  targetProfitRatePercent: '',
  inputProductQuotas: defaultProductQuotas(0),
  outputProductQuotas: defaultProductQuotas(0)
})

const submitting = ref(false)
const amountWarning = ref('')

const isEditMode = computed(() => Boolean(form.id))
const modalTitle = computed(() => isEditMode.value ? '编辑当月客户' : '新增当月客户')
const isFullProfitRate = computed(() => parsePercentInput(form.targetProfitRatePercent) === 1)
const normalizedTargetAmount = computed(() => normalizeAmountInput(form.targetOutputAmount) || 0)
const invoiceRuleCustomer = computed(() => ({
  ...props.customer,
  id: form.id,
  planCustomerId: form.id,
  targetOutputAmount: normalizedTargetAmount.value,
  targetProfitRate: parsePercentInput(form.targetProfitRatePercent),
  companyNameSnapshot: form.companyName,
  companyName: form.companyName,
  regionBucketId: form.regionBucketId
}))
const regionRequired = computed(() => regionOptions.value.length > 0)
const regionOptions = computed(() => {
  const options = [...props.regions]
  if (form.regionBucketId && !options.some(region => String(region.id) === String(form.regionBucketId))) {
    options.push({ id: form.regionBucketId, regionName: '当前区域 #' + form.regionBucketId })
  }
  return options
})

function resetForm() {
  const customer = props.customer || {}
  Object.assign(form, {
    id: customer.id || null,
    customerId: customer.customerId || null,
    companyName: customer.companyName || customer.customerName || customer.name || '',
    registeredRegion: customer.registeredRegion || '',
    regionBucketId: customer.regionBucketId || null,
    targetOutputAmount: customer.targetOutputAmount ?? null,
    targetProfitRatePercent: toPercentInput(customer.targetProfitRate),
    inputProductQuotas: normalizeProductQuotas(customer.inputProductQuotas || fromLegacyProductNames(customer.inputProductNames, customer.targetOutputAmount), customer.targetOutputAmount),
    outputProductQuotas: normalizeProductQuotas(customer.outputProductQuotas || fromLegacyProductNames(customer.outputProductNames, customer.targetOutputAmount), customer.targetOutputAmount)
  })
  amountWarning.value = ''
}

function close() {
  emit('update:modelValue', false)
}

function selectRegion(value) {
  const matchedRegion = props.regions.find(region => String(region.id) === String(value))
  form.regionBucketId = matchedRegion ? matchedRegion.id : null
}

function validateAmount() {
  const normalizedAmount = normalizeAmountInput(form.targetOutputAmount)
  const inputAmount = Number(String(form.targetOutputAmount ?? '').trim())
  if (!Number.isFinite(inputAmount) || normalizedAmount === null || normalizedAmount <= 0) {
    ElMessage.warning('请填写有效的目标额度')
    return null
  }
  if (inputAmount !== normalizedAmount) {
    const warning = '金额将按 100 元取整为 ' + formatInvoiceAmount(normalizedAmount)
    if (amountWarning.value !== warning) {
      amountWarning.value = warning
      form.targetOutputAmount = normalizedAmount
      ElMessage.warning(warning)
      return null
    }
  }
  return normalizedAmount
}

function fromLegacyProductNames(productNames, targetAmount) {
  if (!Array.isArray(productNames) || productNames.length === 0) return defaultProductQuotas(targetAmount)
  return [{ productNames, displayAmount: normalizeAmountInput(targetAmount) || 0 }]
}

function validateProductQuotas(value, label, targetAmount) {
  const errors = validateProductQuotaList(value, targetAmount)
  if (errors.length === 0 && quotaTotalMatched(value, targetAmount)) return true
  ElMessage.warning(errors[0] || label)
  return false
}

function validate() {
  if (!props.planId) {
    ElMessage.warning('请选择月度方案')
    return null
  }
  if (!isEditMode.value && !form.companyName.trim()) {
    ElMessage.warning('请填写公司名称')
    return null
  }
  if (regionRequired.value && (form.regionBucketId === null || form.regionBucketId === undefined || form.regionBucketId === '')) {
    ElMessage.warning('请选择名单区域')
    return null
  }
  const targetOutputAmount = validateAmount()
  if (targetOutputAmount === null) return null
  const targetProfitRate = parsePercentInput(form.targetProfitRatePercent)
  if (!Number.isFinite(targetProfitRate) || targetProfitRate < 0) {
    ElMessage.warning('请填写有效的利润率')
    return null
  }
  if (!validateProductQuotas(form.inputProductQuotas, '进项品名组结构', targetOutputAmount)) return null
  if (!validateProductQuotas(form.outputProductQuotas, '销项品名组结构', targetOutputAmount)) return null
  return { targetOutputAmount, targetProfitRate }
}

function createPayload() {
  const targetOutputAmount = normalizeAmountInput(form.targetOutputAmount)
  return {
    customerId: form.customerId || null,
    companyName: form.companyName.trim(),
    registeredRegion: form.registeredRegion.trim(),
    regionBucketId: form.regionBucketId,
    targetOutputAmount,
    targetProfitRate: parsePercentInput(form.targetProfitRatePercent),
    inputProductQuotas: normalizeProductQuotas(form.inputProductQuotas, targetOutputAmount),
    outputProductQuotas: normalizeProductQuotas(form.outputProductQuotas, targetOutputAmount)
  }
}

function updatePayload() {
  const targetOutputAmount = normalizeAmountInput(form.targetOutputAmount)
  return {
    regionBucketId: form.regionBucketId,
    targetOutputAmount: normalizeAmountInput(form.targetOutputAmount),
    targetProfitRate: parsePercentInput(form.targetProfitRatePercent),
    inputProductQuotas: normalizeProductQuotas(form.inputProductQuotas, targetOutputAmount),
    outputProductQuotas: normalizeProductQuotas(form.outputProductQuotas, targetOutputAmount)
  }
}

async function submit() {
  if (submitting.value) return
  const parsed = validate()
  if (!parsed) return
  submitting.value = true
  try {
    const payload = isEditMode.value ? updatePayload() : createPayload()
    const sync = isEditMode.value
      ? await updateEnterpriseInvoicePlanCustomer(props.planId, form.id, payload)
      : await addEnterpriseInvoicePlanCustomer(props.planId, payload)
    ElMessage.success(isEditMode.value ? '客户已更新' : '客户已添加')
    emit('saved', sync, payload)
    close()
  } catch (error) {
    console.error('Failed to save enterprise invoice customer', error)
    ElMessage.error(error.message || '保存客户失败')
  } finally {
    submitting.value = false
  }
}

watch(() => props.modelValue, value => {
  if (value) resetForm()
})

watch(() => props.customer, () => {
  if (props.modelValue) resetForm()
})
</script>
